import { useState, useMemo } from 'react';
import { T, Var } from 'gt-react';
import { format, formatDistanceToNow } from 'date-fns';
import {
  EnvelopeSimpleIcon,
  PencilIcon,
  PlusIcon,
  LinkIcon,
  CircleIcon,
  CircleIcon as CircleDotIcon,
  EyeIcon,
} from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { useActivityLog } from '@/components/activity-log/useActivityLog';
import { orderStageActivityDescription } from '@/lib/order-stage';
import { useActivityGraphContext } from '../ActivityGraphContext';
import type { SystemEntity } from '@erp/shared-types';
import type { ActivityItem, ActivityItemType } from '@erp/rpc';

interface ActivityLogTabProps {
  entityType: SystemEntity;
  entityId: string;
}

type GroupedActivityItem =
  ActivityItem | { type: 'email_group'; emails: ActivityItem[]; id: string };

function isEmailGroup(
  item: GroupedActivityItem,
): item is { type: 'email_group'; emails: ActivityItem[]; id: string } {
  return 'emails' in item && item.type === 'email_group';
}

function getActivityTypeConfig(type: ActivityItemType) {
  switch (type) {
    case 'created':
      return {
        icon: PlusIcon,
        bgColor: 'bg-green-100 dark:bg-green-600',
        iconColor: 'text-green-600 dark:text-white',
        ringColor: 'ring-green-400',
        label: 'Created',
      };
    case 'update':
      return {
        icon: PencilIcon,
        bgColor: 'bg-blue-100 dark:bg-blue-600',
        iconColor: 'text-blue-600 dark:text-white',
        ringColor: 'ring-blue-400',
        label: 'Updated',
      };
    case 'status_change':
      return {
        icon: CircleDotIcon,
        bgColor: 'bg-purple-100 dark:bg-purple-600',
        iconColor: 'text-purple-600 dark:text-white',
        ringColor: 'ring-purple-400',
        label: 'Status changed',
      };
    case 'email':
      return {
        icon: EnvelopeSimpleIcon,
        bgColor: 'bg-gray-100 dark:bg-zinc-700',
        iconColor: 'text-gray-600 dark:text-white',
        ringColor: 'ring-gray-400',
        label: 'Email',
      };
    case 'linked':
      return {
        icon: LinkIcon,
        bgColor: 'bg-gray-100 dark:bg-zinc-700',
        iconColor: 'text-gray-600 dark:text-white',
        ringColor: 'ring-gray-400',
        label: 'Linked',
      };
    default:
      return {
        icon: CircleIcon,
        bgColor: 'bg-gray-100 dark:bg-zinc-700',
        iconColor: 'text-gray-600 dark:text-white',
        ringColor: 'ring-gray-400',
        label: 'Activity',
      };
  }
}

/**
 * Curated overrides for non-obvious camelCase field names. Anything not in
 * here falls through to `humanizeFieldKey` which splits on capital letters.
 */
const FIELD_LABEL_OVERRIDES: Readonly<Record<string, string>> = {
  shipDate: 'ship date',
  deliveryDate: 'delivery date',
  purchaseOrderNumber: 'PO number',
  shipToLocationId: 'ship-to location',
  billToLocationId: 'bill-to location',
};

/**
 * Convert a camelCase / PascalCase identifier into a human-readable label —
 * "shippingMethod" → "shipping method", "freightCarrierInstructions" →
 * "freight carrier instructions". Single-word identifiers pass through
 * lowercased.
 */
function humanizeFieldKey(field: string): string {
  return field
    .replace(/([A-Z])/g, ' $1')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function labelForFieldKey(field: string): string {
  return FIELD_LABEL_OVERRIDES[field] ?? humanizeFieldKey(field);
}

function getUpdateDescriptionFromPath(
  path: string | undefined,
  changes: Record<string, unknown> | undefined,
): string | null {
  if (!path) return null;

  if (path.includes('/customer-products/match')) {
    return 'matched line item to product';
  }
  if (path.includes('/line-item')) {
    const after = changes?.after as Record<string, unknown> | undefined;
    const before = changes?.before as Record<string, unknown> | undefined;
    if (after && before) {
      const changedFields: string[] = [];
      if (after.quantity !== before.quantity) changedFields.push('quantity');
      if (after.unitPrice !== before.unitPrice) changedFields.push('price');
      if (after.customerDescription !== before.customerDescription)
        changedFields.push('description');
      if (after.customerIdentifier !== before.customerIdentifier)
        changedFields.push('identifier');
      if (changedFields.length > 0) {
        return `updated line item ${changedFields.join(', ')}`;
      }
    }
    return 'updated line item';
  }
  if (path.endsWith('/discounts')) {
    return 'created a discount';
  }
  if (path.includes('/customer')) {
    return 'changed customer';
  }
  if (path.includes('/field')) {
    const field = changes?.field as string | undefined;
    if (field) {
      return `updated ${labelForFieldKey(field)}`;
    }
  }

  return null;
}

function formatActivityDescription(item: ActivityItem): string {
  const { type, metadata, actor } = item;
  const actorName = actor?.name || 'System';

  switch (type) {
    case 'created':
      return `${actorName} created this`;
    case 'update': {
      const path = metadata?.path as string | undefined;
      const changes = metadata?.changes as Record<string, unknown> | undefined;
      const description = getUpdateDescriptionFromPath(path, changes);
      if (description) {
        return `${actorName} ${description}`;
      }
      const field = metadata?.field as string | undefined;
      return `${actorName} updated ${field || 'details'}`;
    }
    case 'status_change': {
      // Funnel lifecycle transitions carry the stage in metadata (no path).
      const stage = metadata?.stage as string | undefined;
      if (stage) {
        return orderStageActivityDescription(stage);
      }

      const path = metadata?.path as string | undefined;
      const changes = metadata?.changes as Record<string, unknown> | undefined;

      if (path?.includes('/discounts/approval/decision/bulk')) {
        return `${actorName} bulk-approved discounts`;
      }
      if (path?.includes('/discounts/approval/decision')) {
        const decision =
          typeof changes?.decision === 'string' ? changes.decision : null;
        if (decision === 'APPROVED') return `${actorName} approved a discount`;
        if (decision === 'REJECTED') return `${actorName} rejected a discount`;
        if (decision === 'REQUEST_CHANGES')
          return `${actorName} requested changes on a discount`;
        return `${actorName} submitted a discount approval decision`;
      }
      if (path?.includes('/discounts/disable')) {
        return `${actorName} disabled a discount`;
      }
      if (path?.includes('/orderApprovals/decision')) {
        const decision =
          typeof changes?.decision === 'string' ? changes.decision : null;
        if (decision === 'APPROVED') return `${actorName} approved this order`;
        if (decision === 'REJECTED') return `${actorName} rejected this order`;
        return `${actorName} submitted an order approval decision`;
      }
      if (path?.includes('/approve')) {
        return `${actorName} approved`;
      }
      if (path?.includes('/reject')) {
        return `${actorName} rejected`;
      }
      const from = metadata?.from as string | undefined;
      const to = metadata?.to as string | undefined;
      if (from && to) {
        return `${actorName} moved from ${from} to ${to}`;
      }
      return `${actorName} changed status`;
    }
    case 'email': {
      const emailFrom = metadata?.from as string | undefined;
      const subject = metadata?.subject as string | undefined;
      return `${emailFrom || 'Unknown'} · ${subject || 'No subject'}`;
    }
    case 'linked': {
      const linkedType = metadata?.linkedEntityType as string | undefined;
      return `${actorName} linked ${linkedType || 'entity'}`;
    }
    default:
      return `${actorName} performed action`;
  }
}

function getActorInitials(name?: string): string {
  if (!name) return 'S';
  const parts = name.split(' ');
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

function formatFullDateTime(timestamp: string | Date): string {
  const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
  return format(date, 'yyyy-MM-dd h:mm a');
}

function groupConsecutiveEmails(items: ActivityItem[]): GroupedActivityItem[] {
  const result: GroupedActivityItem[] = [];
  let currentEmailGroup: ActivityItem[] = [];

  for (const item of items) {
    if (item.type === 'email') {
      currentEmailGroup.push(item);
    } else {
      if (currentEmailGroup.length > 1) {
        result.push({
          type: 'email_group',
          emails: currentEmailGroup,
          id: `email-group-${currentEmailGroup[0].id}`,
        });
      } else if (currentEmailGroup.length === 1) {
        result.push(currentEmailGroup[0]);
      }
      currentEmailGroup = [];
      result.push(item);
    }
  }

  if (currentEmailGroup.length > 1) {
    result.push({
      type: 'email_group',
      emails: currentEmailGroup,
      id: `email-group-${currentEmailGroup[0].id}`,
    });
  } else if (currentEmailGroup.length === 1) {
    result.push(currentEmailGroup[0]);
  }

  return result;
}

function ActivityItemRow({
  item,
  onViewEmail,
}: {
  item: ActivityItem;
  onViewEmail?: (emailId: string) => void;
}) {
  const config = getActivityTypeConfig(item.type);
  const Icon = config.icon;
  const description = formatActivityDescription(item);
  const timeAgo = formatDistanceToNow(new Date(item.timestamp), {
    addSuffix: false,
  });
  const fullDateTime = formatFullDateTime(item.timestamp);

  const emailId =
    item.type === 'email' ? (item.metadata?.emailId as string) : null;
  const isClickable = !!emailId && !!onViewEmail;

  const content = (
    <>
      <div
        className={cn(
          'flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
          config.bgColor,
        )}
      >
        <Icon className={cn('h-3.5 w-3.5', config.iconColor)} />
      </div>

      <div className="flex flex-1 flex-col gap-0.5 min-w-0">
        <div className="flex items-center gap-2">
          {item.actor && (
            <Avatar className="h-5 w-5 shrink-0">
              <AvatarFallback className="text-[10px] bg-gray-200 text-gray-700 dark:bg-zinc-700 dark:text-zinc-300">
                {getActorInitials(item.actor.name)}
              </AvatarFallback>
            </Avatar>
          )}
          <span className="text-sm text-gray-700 dark:text-zinc-300 truncate">
            {description}
          </span>
        </div>
        <span
          className="text-xs text-gray-500 dark:text-zinc-500"
          title={fullDateTime}
        >
          <T>
            <Var>{timeAgo}</Var> ago
          </T>
        </span>
      </div>
      {isClickable && (
        <EyeIcon className="h-4 w-4 shrink-0 text-gray-400 dark:text-zinc-500" />
      )}
    </>
  );

  if (isClickable) {
    return (
      <button
        type="button"
        className="flex items-start gap-3 py-2 w-full text-left rounded hover:bg-gray-50 dark:hover:bg-zinc-800 transition-colors -mx-1 px-1"
        onClick={() => onViewEmail(emailId)}
      >
        {content}
      </button>
    );
  }

  return <div className="flex items-start gap-3 py-2">{content}</div>;
}

function EmailGroupExpandedRow({
  group,
  onViewEmail,
  onViewThread,
}: {
  group: { emails: ActivityItem[]; id: string };
  onViewEmail?: (emailId: string) => void;
  onViewThread?: (group: { emails: ActivityItem[] }) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const emailCount = group.emails.length;
  const config = getActivityTypeConfig('email');
  const Icon = config.icon;
  const latestEmail = group.emails[group.emails.length - 1];
  const timeAgo = formatDistanceToNow(new Date(latestEmail.timestamp), {
    addSuffix: false,
  });
  const fullDateTime = formatFullDateTime(latestEmail.timestamp);

  return (
    <div className="py-2">
      <div className="flex w-full items-start gap-3">
        <div
          className={cn(
            'flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
            config.bgColor,
          )}
        >
          <Icon className={cn('h-3.5 w-3.5', config.iconColor)} />
        </div>

        <div className="flex flex-1 flex-col gap-0.5 min-w-0">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setIsOpen(!isOpen)}
              className="text-sm text-gray-700 dark:text-zinc-300 hover:underline"
            >
              <T>
                <Var>{emailCount}</Var> emails ·{' '}
                <Var>{isOpen ? 'hide' : 'show'}</Var>
              </T>
            </button>
            {onViewThread && (
              <button
                type="button"
                onClick={() => onViewThread(group)}
                className="text-xs text-primary hover:underline flex items-center gap-1"
              >
                <EyeIcon className="h-3 w-3" />
                <T>view thread</T>
              </button>
            )}
          </div>
          <span
            className="text-xs text-gray-500 dark:text-zinc-500"
            title={fullDateTime}
          >
            <T>
              <Var>{timeAgo}</Var> ago
            </T>
          </span>
        </div>
      </div>

      {isOpen && (
        <div className="mt-2 space-y-1 pl-9 border-l-2 border-gray-200 dark:border-zinc-700 ml-3">
          {group.emails.map((email) => (
            <ActivityItemRow
              key={email.id}
              item={email}
              onViewEmail={onViewEmail}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-3 p-3">
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton className="h-6 w-6 rounded-full" />
          <div className="flex-1 space-y-1">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-3 w-16" />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex items-center justify-center py-8 text-sm text-gray-500 dark:text-zinc-500">
      <T>No activity yet</T>
    </div>
  );
}

export function ActivityLogTab({ entityType, entityId }: ActivityLogTabProps) {
  const context = useActivityGraphContext();
  const { data, isLoading, isFetching, error } = useActivityLog(
    entityType,
    entityId,
  );
  const [, setPinnedItemId] = useState<string | null>(null);

  const handleViewEmail = (emailId: string) => {
    if (context) {
      context.viewEmail(emailId);
    }
    setPinnedItemId(null);
  };

  const handleViewEmailGroup = (group: { emails: ActivityItem[] }) => {
    const emailIds = group.emails
      .map((email) => email.metadata?.emailId as string | undefined)
      .filter((id): id is string => !!id);
    if (emailIds.length > 0 && context) {
      context.viewEmailThread(emailIds);
    }
    setPinnedItemId(null);
  };

  const groupedItems = useMemo(() => {
    if (!data?.items?.length) return [];
    return groupConsecutiveEmails(data.items);
  }, [data?.items]);

  if (error) {
    return (
      <div className="flex items-center justify-center p-4 text-sm text-destructive">
        <T>Failed to load activity</T>
      </div>
    );
  }

  const showLoading = isLoading || (isFetching && !data);

  if (showLoading) {
    return <LoadingSkeleton />;
  }

  if (!groupedItems.length) {
    return <EmptyState />;
  }

  return (
    <div className="p-3 space-y-1 divide-y divide-gray-100 dark:divide-zinc-800">
      {groupedItems.map((item) => {
        if (isEmailGroup(item)) {
          return (
            <EmailGroupExpandedRow
              key={item.id}
              group={item}
              onViewEmail={handleViewEmail}
              onViewThread={handleViewEmailGroup}
            />
          );
        }
        return (
          <ActivityItemRow
            key={item.id}
            item={item}
            onViewEmail={handleViewEmail}
          />
        );
      })}
    </div>
  );
}
